import { useEffect, useRef } from 'react'
import { savedViewsApi } from '@/lib/api/saved-views.api'
import { createLogger } from '@/lib/logger'
import {
  buildViewShareUrl,
  encodeViewParam,
  parseViewParam,
  resolveInlineView,
  VIEW_URL_PARAM,
} from '@/lib/views/view-url'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import type { ModuleTypeEnum, ViewConfig } from '@scrambl/shared'

const logger = createLogger('hooks/useViewUrlState')

interface UseViewUrlStateOptions {
  moduleType: ModuleTypeEnum
  /** Current table state, used to build the share link */
  getCurrentConfig: () => ViewConfig | null
  /** Applies an incoming link's state to the table */
  applyConfig: (config: ViewConfig) => Promise<void> | void
  /**
   * Id of a saved view that fully describes the current state — when set, the
   * share link is the short `?view=<id>` form instead of an inline payload.
   */
  preferSavedViewId?: string | null
  /** Optional name embedded in inline links, purely for readability */
  viewName?: string
}

interface UseViewUrlStateResult {
  /** Full URL for the current view, or null when there is nothing to share */
  getShareUrl: () => string | null
  /** True while an incoming `?view=<id>` link is still resolving */
  isResolvingSharedView: boolean
}

/**
 * Read and write the `?view=` share parameter for a module list surface.
 *
 * Mirrors the ticket query bar's `?q=` pattern: state that a colleague can
 * paste. Incoming links are applied exactly once per (param, organization)
 * pair — including the organization means switching orgs re-evaluates a stale
 * link and rejects it rather than repainting the new org's table with the old
 * org's filters.
 */
export function useViewUrlState({
  moduleType,
  getCurrentConfig,
  applyConfig,
  preferSavedViewId,
  viewName,
}: UseViewUrlStateOptions): UseViewUrlStateResult {
  const [searchParams, setSearchParams] = useSearchParams()
  const { currentOrganization } = useOrganizationStore()
  const organizationId = currentOrganization?.id

  const rawParam = searchParams.get(VIEW_URL_PARAM)
  const parsed = parseViewParam(rawParam)
  const appliedKeyRef = useRef<string | null>(null)

  const savedViewId = parsed?.kind === 'saved' ? parsed.id : null

  const { data: sharedView, isLoading: isResolvingSharedView } = useQuery({
    queryKey: ['saved-view', savedViewId, organizationId],
    queryFn: () => savedViewsApi.get(savedViewId as string),
    enabled: !!savedViewId && !!organizationId,
    retry: false,
    staleTime: 60_000,
  })

  const clearParam = () => {
    setSearchParams(
      params => {
        const next = new URLSearchParams(params)
        next.delete(VIEW_URL_PARAM)
        return next
      },
      { replace: true },
    )
  }

  useEffect(() => {
    if (!rawParam || !organizationId) return
    // Re-keyed on the organization so an org switch re-runs the safety checks.
    const key = `${organizationId}:${rawParam}`
    if (appliedKeyRef.current === key) return

    if (!parsed) {
      appliedKeyRef.current = key
      logger.warn('[useViewUrlState] Ignoring unreadable view link')
      clearParam()
      return
    }

    if (parsed.kind === 'saved') {
      if (isResolvingSharedView) return
      appliedKeyRef.current = key
      if (!sharedView) {
        toast.error('That view link is no longer available here')
        clearParam()
        return
      }
      if (!sharedView.modules.includes(moduleType)) {
        toast.error('That view link belongs to a different module')
        clearParam()
        return
      }
      void applyConfig(sharedView.config)
      return
    }

    appliedKeyRef.current = key
    const resolved = resolveInlineView(parsed.payload, {
      organizationId,
      moduleType,
    })
    if ('rejected' in resolved) {
      if (resolved.rejected === 'organization') {
        toast.error('That view link belongs to a different organization')
      } else if (resolved.rejected === 'module') {
        toast.error('That view link belongs to a different module')
      }
      clearParam()
      return
    }

    void applyConfig(resolved.config)
    // `applyConfig` identity changes every render on table pages; the applied
    // key guard is what makes this idempotent, so it is deliberately not a
    // dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawParam, organizationId, moduleType, sharedView, isResolvingSharedView])

  const getShareUrl = (): string | null => {
    if (typeof window === 'undefined') return null

    if (preferSavedViewId) {
      return buildViewShareUrl(window.location.href, preferSavedViewId)
    }

    const config = getCurrentConfig()
    if (!config || !organizationId) return null

    const encoded = encodeViewParam({
      organizationId,
      moduleType,
      config,
      name: viewName,
    })
    if (!encoded) return null

    return buildViewShareUrl(window.location.href, encoded)
  }

  return {
    getShareUrl,
    isResolvingSharedView: !!savedViewId && isResolvingSharedView,
  }
}
